/**
 * Invoice schemas — invoicing lifecycle lives in OPS (draft → issue → send →
 * payments → paid/overdue/cancel/void), with time entries linked to invoices
 * so billable work can't be invoiced twice.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, moneySchema, pageInput, sortOrderSchema, currencySchema } from './common';

/** Date inputs may arrive date-only (YYYY-MM-DD, from <input type="date">) —
 *  normalize to a full ISO timestamp before the strict datetime check. */
const dateLikeSchema = z.preprocess(
  (val) => (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val) ? `${val}T00:00:00.000Z` : val),
  iso8601Schema,
);

export const invoiceStatusSchema = z.enum([
  'DRAFT',
  'ISSUED',
  'SENT',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
  'CANCELLED',
  'VOID',
]);

export const invoiceLineItemSchema = z.object({
  description: z.string().min(1).max(200),
  quantity: z.number().positive(),
  unitPrice: moneySchema,                   // per unit, minor units
  amount: moneySchema,                     // = quantity × unitPrice, server-computed
  /** VAT rate in whole percent (0 = exempt/zero-rated, 5 = UAE, 20 = UK). */
  vatRate: z.number().int().min(0).max(100).default(0),
  complianceItemId: idSchema.nullable().optional(),
  timeEntryIds: z.array(idSchema).max(500).optional(),
});

export const invoicePaymentSchema = z.object({
  id: idSchema,
  amount: moneySchema,
  paidAt: iso8601Schema,
  method: z.string().max(100).nullable(),
  note: z.string().max(500).nullable(),
  createdById: idSchema,
  createdAt: iso8601Schema,
});
export type InvoicePaymentOutput = z.infer<typeof invoicePaymentSchema>;

export const invoicePaymentInputSchema = z.object({
  amount: z.number().int().positive().max(2_147_483_647),
  paidAt: dateLikeSchema,
  method: z.string().max(100).nullish(),
  note: z.string().max(500).nullish(),
  idempotencyKey: idSchema.nullish(),
});
export type InvoicePaymentInput = z.infer<typeof invoicePaymentInputSchema>;

export const invoiceScopeSnapshotSchema = z.object({
  subject: z.string().max(200),
  body: z.string().max(10000),
  items: z.array(z.object({
    title: z.string().max(200),
    description: z.string().max(1000).nullable(),
  }).strip()).max(100),
}).strip();
export type InvoiceScopeSnapshot = z.infer<typeof invoiceScopeSnapshotSchema>;

export const invoiceOutputSchema = z.object({
  id: idSchema,
  clientId: idSchema.nullable(),
  proposalId: idSchema.nullable(),
  manualScheduleId: idSchema.nullable().optional(),
  brandingProfileId: idSchema.nullable().optional(),
  recurringPeriodStart: iso8601Schema.nullable(),
  engagementId: idSchema.nullable(),
  complianceClientId: idSchema.nullable(),
  invoiceNumber: z.string().regex(/^INV-\d{4,}$/),
  status: invoiceStatusSchema,
  issueDate: iso8601Schema,
  dueDate: iso8601Schema,
  paidDate: iso8601Schema.nullable(),
  subtotal: moneySchema,
  tax: moneySchema.default({ amount: 0, currency: 'USD' }),
  total: moneySchema,
  lineItems: z.array(invoiceLineItemSchema).min(1),
  zohoInvoiceId: z.string().nullable(),
  notes: z.string().max(2000).nullable(),
  scopeSnapshot: invoiceScopeSnapshotSchema.nullable().default(null),
  pdfUrl: z.string().url().nullable(),
  /** Payment-reminder escalation: 0=none sent, 1/2/3 = due+7/+14/+21 sent. */
  reminderStage: z.number().int().min(0).max(3),
  lastReminderAt: iso8601Schema.nullable(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type InvoiceOutput = z.infer<typeof invoiceOutputSchema>;

/** List row — no line items or scope; adds display names + amount paid so far. */
export const invoiceListItemSchema = invoiceOutputSchema.omit({ lineItems: true, scopeSnapshot: true }).extend({
  engagementTitle: z.string().nullable(),
  clientName: z.string().nullable(),
  paidAmount: z.number().int().nonnegative(),
});
export type InvoiceListItem = z.infer<typeof invoiceListItemSchema>;

/** Detail view — full output plus payments, names and running paid total. */
export const invoiceDetailOutputSchema = invoiceOutputSchema.extend({
  engagementTitle: z.string().nullable(),
  clientName: z.string().nullable(),
  complianceClientName: z.string().nullable(),
  /** CRM client resolved from its direct link or legacy source context. */
  crmClientId: idSchema.nullable(),
  crmClientName: z.string().nullable(),
  ownerId: idSchema.nullable(),
  /** Token for the public /pay/<token> view — assigned on first PDF generation. */
  publicToken: z.string().nullable(),
  payments: z.array(invoicePaymentSchema),
  paidAmount: z.number().int().nonnegative(),
  /** True once a post-issue edit (ISSUED/SENT/OVERDUE/PARTIALLY_PAID) is on the activity log. */
  editedAfterIssue: z.boolean(),
});
export type InvoiceDetailOutput = z.infer<typeof invoiceDetailOutputSchema>;

/* ── Invoice branding (Admin singleton) ──────────────────────────────────── */

export const brandingAccentSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a six-digit hex colour, e.g. #10b981');
const bankText = (max: number) => z.string().trim().max(max);
export const bankProfileSchema = z.object({
  id: idSchema,
  label: bankText(100).min(1, 'Bank label is required'),
  bankName: bankText(150).min(1, 'Bank name is required'),
  accountHolder: bankText(200).min(1, 'Account holder is required'),
  accountNumber: bankText(100).default(''),
  iban: bankText(100).default(''),
  swiftBic: bankText(50).default(''),
  routingCode: bankText(100).default(''),
  branch: bankText(200).default(''),
  currencyLabel: bankText(50).default(''),
  /** Real invoice currency used for payment-instruction filtering. */
  currency: currencySchema.nullable().default(null),
  publicNotes: bankText(1000).default(''),
  active: z.boolean().default(true),
}).strict();
export type BankProfile = z.infer<typeof bankProfileSchema>;
export const bankProfilesSchema = z.array(bankProfileSchema.refine((b) => !!(b.accountNumber || b.iban), {
  message: 'Enter an account number or IBAN', path: ['accountNumber'],
})).max(10).refine((banks) => new Set(banks.map((b) => b.id)).size === banks.length, 'Bank ids must be unique');
export const managedBankProfileSchema = bankProfileSchema.extend({ currency: currencySchema });
export const managedBankProfilesSchema = z.array(managedBankProfileSchema.refine((b) => !!(b.accountNumber || b.iban), {
  message: 'Enter an account number or IBAN', path: ['accountNumber'],
})).max(10).refine((banks) => new Set(banks.map((b) => b.id)).size === banks.length, 'Bank ids must be unique');
export const paymentInstructionSchema = bankProfileSchema.omit({ id: true, active: true }).strip();
export type PaymentInstruction = z.infer<typeof paymentInstructionSchema>;
const identityFields = {
  companyName: z.string().max(200).nullable(),
  addressBlock: z.string().max(1000).nullable(),
  contactEmail: z.string().email().max(254).nullable(),
  contactPhone: z.string().max(80).nullable(),
  website: z.string().url().max(300).refine((v) => /^https?:\/\//i.test(v), 'Website must use http or https').nullable(),
  taxRegistration: z.string().max(100).nullable(),
};
export const publicDocumentBrandingSchema = z.object({
  ...identityFields, companyName: z.string(), logoUrl: z.string().nullable(),
  accentColor: brandingAccentSchema, footerNote: z.string(),
});
export type PublicDocumentBranding = z.infer<typeof publicDocumentBrandingSchema>;
export const invoiceBrandingOutputSchema = z.object({
  ...identityFields, logoUrl: z.string().nullable(), footerNote: z.string().nullable(),
  accentColor: brandingAccentSchema, bankProfiles: bankProfilesSchema,
  updatedAt: iso8601Schema,
});
export type InvoiceBrandingOutput = z.infer<typeof invoiceBrandingOutputSchema>;
export const updateInvoiceBrandingInputSchema = z.object({
  ...identityFields, footerNote: z.string().max(500).nullable(),
  accentColor: brandingAccentSchema, bankProfiles: bankProfilesSchema,
}).partial().strict();
export type UpdateInvoiceBrandingInput = z.infer<typeof updateInvoiceBrandingInputSchema>;

export const invoiceBrandingProfileOutputSchema = z.object({
  id: idSchema,
  name: z.string().min(1).max(120),
  active: z.boolean(),
  isDefault: z.boolean(),
  ...identityFields,
  logoUrl: z.string().nullable(),
  footerNote: z.string().nullable(),
  accentColor: brandingAccentSchema,
  bankProfiles: bankProfilesSchema,
  updatedAt: iso8601Schema,
});
export type InvoiceBrandingProfileOutput = z.infer<typeof invoiceBrandingProfileOutputSchema>;
export const createInvoiceBrandingProfileInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  active: z.boolean().default(true),
  isDefault: z.boolean().default(false),
  ...identityFields,
  footerNote: z.string().max(500).nullable(),
  accentColor: brandingAccentSchema,
  bankProfiles: managedBankProfilesSchema,
}).strict();
export type CreateInvoiceBrandingProfileInput = z.infer<typeof createInvoiceBrandingProfileInputSchema>;
export const updateInvoiceBrandingProfileInputSchema = createInvoiceBrandingProfileInputSchema.partial().strict();
export type UpdateInvoiceBrandingProfileInput = z.infer<typeof updateInvoiceBrandingProfileInputSchema>;

export const proposalBrandingOutputSchema = z.object({
  ...identityFields, logoUrl: z.string().nullable(),
  inheritedAccentColor: brandingAccentSchema, inheritedFooterNote: z.string().nullable(),
  proposalAccentColor: brandingAccentSchema.nullable(), proposalFooterNote: z.string().max(500).nullable(),
  updatedAt: iso8601Schema,
});
export type ProposalBrandingOutput = z.infer<typeof proposalBrandingOutputSchema>;
export const updateProposalBrandingInputSchema = z.object({
  proposalAccentColor: brandingAccentSchema.nullable().optional(),
  proposalFooterNote: z.string().max(500).nullable().optional(),
}).strict();

/** Public, token-gated invoice view (GET /v1/public/invoice/:token) — no internal ids. */
export const publicInvoiceOutputSchema = z.object({
  invoiceNumber: z.string(),
  status: invoiceStatusSchema,
  issueDate: iso8601Schema,
  dueDate: iso8601Schema,
  paidDate: iso8601Schema.nullable(),
  clientName: z.string().nullable(),
  engagementTitle: z.string().nullable(),
  subtotal: moneySchema,
  tax: moneySchema,
  total: moneySchema,
  paidAmount: z.number().int().nonnegative(),
  balanceDue: z.number().int(),
  lineItems: z.array(z.object({
    description: z.string(),
    amount: moneySchema,
    vatRate: z.number().int().min(0).max(100),
  })).min(1),
  payments: z.array(z.object({
    amount: moneySchema,
    paidAt: iso8601Schema,
    method: z.string().nullable(),
  })),
  notes: z.string().nullable(),
  scopeSnapshot: invoiceScopeSnapshotSchema.nullable().default(null),
  branding: publicDocumentBrandingSchema,
  paymentInstructions: z.array(paymentInstructionSchema),
});
export type PublicInvoiceOutput = z.infer<typeof publicInvoiceOutputSchema>;

export const invoiceBillingFrequencySchema = z.enum(['MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL']);
export const invoiceRecurrenceInputSchema = z.object({
  frequency: invoiceBillingFrequencySchema,
  startDate: dateLikeSchema,
  requestKey: idSchema,
}).strict();

export const createInvoiceInputSchema = z.object({
  clientId: idSchema.nullable().optional(),
  engagementId: idSchema.nullable().optional(),
  complianceClientId: idSchema.nullable().optional(),
  brandingProfileId: idSchema.nullable().optional(),
  dueDate: dateLikeSchema,
  lineItems: z.array(invoiceLineItemSchema).min(1).max(100),
  notes: z.string().max(2000).nullable().optional(),
  recurrence: invoiceRecurrenceInputSchema.optional(),
}).refine((v) => v.clientId || v.engagementId || v.complianceClientId, {
  message: 'An invoice needs a CRM client, engagement or compliance client',
});
export type CreateInvoiceInput = z.infer<typeof createInvoiceInputSchema>;

/** Editable statuses permit due date, notes and a full line-item replacement. */
export const updateInvoiceInputSchema = z.object({
  brandingProfileId: idSchema.nullable().optional(),
  dueDate: dateLikeSchema.optional(),
  notes: z.string().max(2000).nullable().optional(),
  lineItems: z.array(invoiceLineItemSchema).min(1).max(100).optional(),
});
export type UpdateInvoiceInput = z.infer<typeof updateInvoiceInputSchema>;

export const invoiceSortBySchema = z.enum(['issueDate', 'dueDate', 'total', 'status']).default('issueDate');

export const listInvoicesInputSchema = pageInput.extend({
  engagementId: idSchema.optional(),
  complianceClientId: idSchema.optional(),
  /** CRM client — matches invoices via the engagement OR the compliance mirror. */
  clientId: idSchema.optional(),
  status: invoiceStatusSchema.optional(),
  from: iso8601Schema.optional(),
  to: iso8601Schema.optional(),
  sortBy: invoiceSortBySchema.optional(),
  sortOrder: sortOrderSchema.optional(),
});
export type ListInvoicesInput = z.infer<typeof listInvoicesInputSchema>;

export const receivableEntrySchema = z.object({
  /** Invoice/payment UUID or the invoice UUID plus a reversal suffix. */
  id: z.string(),
  invoiceId: idSchema,
  date: iso8601Schema,
  type: z.enum(['ISSUED', 'PAYMENT', 'REVERSAL']),
  reference: z.string(),
  details: z.string().nullable(),
  debit: z.number().int().nonnegative(),
  credit: z.number().int().nonnegative(),
  balance: z.number().int(),
  status: invoiceStatusSchema,
  dueDate: iso8601Schema,
});
export type ReceivableEntry = z.infer<typeof receivableEntrySchema>;
export const receivableGroupSchema = z.object({
  currency: currencySchema,
  invoiced: z.number().int().nonnegative(),
  received: z.number().int().nonnegative(),
  outstanding: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  openingBalance: z.number().int().nonnegative(),
  closingBalance: z.number().int().nonnegative(),
  entries: z.array(receivableEntrySchema),
});
export type ReceivableGroup = z.infer<typeof receivableGroupSchema>;
export const receivablesOutputSchema = z.object({
  client: z.object({ id: idSchema, clientCode: z.string(), name: z.string(), company: z.string().nullable() }),
  groups: z.array(receivableGroupSchema),
});
export type ReceivablesOutput = z.infer<typeof receivablesOutputSchema>;

export const receivableCurrencySummarySchema = z.object({
  currency: currencySchema,
  invoiced: z.number().int().nonnegative(),
  received: z.number().int().nonnegative(),
  outstanding: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  current: z.number().int().nonnegative(),
  aging1_30: z.number().int().nonnegative(),
  aging31_60: z.number().int().nonnegative(),
  aging61_90: z.number().int().nonnegative(),
  aging90Plus: z.number().int().nonnegative(),
});
export type ReceivableCurrencySummary = z.infer<typeof receivableCurrencySummarySchema>;
export const receivableClientBalanceSchema = receivableCurrencySummarySchema.extend({
  clientId: idSchema.nullable(),
  clientCode: z.string(),
  clientName: z.string(),
  clientCompany: z.string().nullable(),
  openInvoices: z.number().int().nonnegative(),
  oldestDueDate: iso8601Schema.nullable(),
});
export type ReceivableClientBalance = z.infer<typeof receivableClientBalanceSchema>;
export const receivableReportTransactionSchema = receivableEntrySchema.extend({
  currency: currencySchema,
  clientId: idSchema.nullable(),
  clientCode: z.string(),
  clientName: z.string(),
});
export type ReceivableReportTransaction = z.infer<typeof receivableReportTransactionSchema>;
export const receivablesReportOutputSchema = z.object({
  summary: z.array(receivableCurrencySummarySchema),
  clients: z.array(receivableClientBalanceSchema),
  transactions: z.array(receivableReportTransactionSchema),
});
export type ReceivablesReportOutput = z.infer<typeof receivablesReportOutputSchema>;

/** Billable, not-yet-invoiced time entry offered for invoice import. */
export const unbilledTimeEntrySchema = z.object({
  id: idSchema,
  userId: idSchema,
  userDisplayName: z.string(),
  date: iso8601Schema,
  hours: z.number().positive(),
  description: z.string().nullable(),
  complianceItemId: idSchema.nullable(),
  rate: moneySchema.nullable(),
  rateFound: z.boolean(),
});
export type UnbilledTimeEntry = z.infer<typeof unbilledTimeEntrySchema>;

/** WIP aging report. */
export const wipQueryInputSchema = z.object({
  from: iso8601Schema,
  to: iso8601Schema,
  clientIds: z.array(idSchema).optional(),
  serviceTypes: z.array(z.string()).optional(),
  jurisdictions: z.array(z.string()).optional(),
});

export interface WipByClient {
  clientId: string;
  clientName: string;
  hours: number;
  estimatedHours: number;
  varianceHours: number;
  variancePct: number;
  tier: 'EXCELLENT' | 'GOOD' | 'WATCH';
}

export const wipByClientOutputSchema: z.ZodType<readonly WipByClient[]> = z.array(
  z.object({
    clientId: idSchema,
    clientName: z.string(),
    hours: z.number().nonnegative(),
    estimatedHours: z.number().nonnegative(),
    varianceHours: z.number(),
    variancePct: z.number(),
    tier: z.enum(['EXCELLENT', 'GOOD', 'WATCH']),
  }),
);
